import { Loadable } from '@/components/loaders';
import { FrontTable, IconButton, PlusIcon } from '@/lib';
import { EmailSendingDomain } from '@/models/emailSendingDomains';
import { ColumnDef } from '@tanstack/react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { EmailSendingDomainsListActionsCell } from './EmailSendingDomainsListActionsCell';
import { countPassingRecords, formatDomainDateTime, formatLastChecked, getDomainStatusBadgeClass } from './domainUtils';

type EmailSendingDomainsListProps = {
  domains: EmailSendingDomain[];
  isFetching: boolean;
  onAdd: () => void;
  onSelectDomain: (domainId: string) => void;
  onRequestDelete: (domain: EmailSendingDomain) => void;
};

export const EmailSendingDomainsList: FC<EmailSendingDomainsListProps> = ({ domains, isFetching, onAdd, onSelectDomain, onRequestDelete }) => {
  const { t } = useTranslation('campaign');

  const columns = useMemo<ColumnDef<EmailSendingDomain>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('emailWorkspace.domains.table.domain'),
        cell: ({ row }) => <span className='font-semibold text-slate-900'>{row.original.name}</span>,
      },
      {
        accessorKey: 'status',
        header: t('emailWorkspace.domains.table.status'),
        cell: ({ row }) => (
          <span className={twMerge('rounded-full px-2.5 py-1 text-xs font-semibold', getDomainStatusBadgeClass(row.original.status))}>
            {t(`emailWorkspace.domains.status.${row.original.status}`)}
          </span>
        ),
      },
      {
        id: 'records',
        header: t('emailWorkspace.domains.table.records'),
        cell: ({ row }) => {
          const passing = countPassingRecords(row.original.records);
          return (
            <span className='text-secondary-700 text-sm font-medium'>
              {t('emailWorkspace.domains.recordsPassing', {
                passing,
                total: row.original.records.length,
              })}
            </span>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: t('emailWorkspace.domains.table.created'),
        cell: ({ row }) => <span className='text-secondary-600 text-sm'>{formatDomainDateTime(row.original.createdAt)}</span>,
      },
      {
        accessorKey: 'lastCheckedAt',
        header: t('emailWorkspace.domains.table.lastChecked'),
        cell: ({ row }) => (
          <span className='text-secondary-600 text-sm'>{formatLastChecked(row.original.lastCheckedAt, t('emailWorkspace.domains.never'))}</span>
        ),
      },
      {
        id: 'actions',
        header: t('emailWorkspace.domains.table.actions'),
        size: 120,
        maxSize: 120,
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <EmailSendingDomainsListActionsCell domain={row.original} onOpenDetail={onSelectDomain} onRequestDelete={onRequestDelete} />
        ),
      },
    ],
    [onRequestDelete, onSelectDomain, t]
  );

  return (
    <>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
        <div className='space-y-1'>
          <h2 className='text-xl font-bold text-slate-900'>{t('emailWorkspace.domains.listTitle')}</h2>
          <p className='text-secondary-600 text-sm'>{t('emailWorkspace.domains.listDescription')}</p>
        </div>
        <IconButton
          leadingIcon={<PlusIcon className='size-5 text-white-100' />}
          className='inline-flex w-full max-w-[180px] justify-center'
          onClick={onAdd}
          text={t('emailWorkspace.domains.addDomain')}
        />
      </div>

      <Loadable isLoading={isFetching}>
        <FrontTable
          tableData={domains}
          columns={columns}
          notFound={t('emailWorkspace.domains.emptyTitle')}
          onRowClick={(row) => onSelectDomain(row.original.id)}
        />
      </Loadable>
    </>
  );
};
